import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { ProductPrice } from "./ProductPrice";
import Section from "./Section";
import Container from "./Container";
import { HtmlContent } from "./Content";
import { Helmet } from "react-helmet";
import { navigateToQuoteForm } from "./QuoteFormAndSafesitePromise";
import PricesMayVary from "./PricesMayVary";

const PricingBarBase = styled.div`
  ${({ ...props }) => css`
    background-color: white;
    box-shadow: 0 4px 10px rgb(0 0 0 / 30%);
    transition: opacity .25s ease-out;
    position: relative;
    z-index: 1;

    &.should-hide {
      opacity: 0;
      pointer-events: none;
    }

    section {
      padding-top: 0;
      padding-bottom: 0;
    }

    .pricing-bar__wrapper {
      display: grid;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      padding-top: 1rem;
      padding-bottom: 1rem;

      .title {
        font-size: 1.5rem;
        flex-basis: 100%;
        grid-column: 1 / -1;
      }

      div:has(.price) {
        margin-top: 0;
        grid-column: 1/-1
      }

      .add-to-quote {
        width: max-content;
      }

      .prices-may-vary {
        max-width: 10rem
      }
    }

    @media (min-width: 450px) {
      .pricing-bar__wrapper {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      .pricing-bar__wrapper {
        grid-auto-flow: column;
        grid-template-columns: initial;

        .title {
          grid-column: 1;
          grid-row: 1;
          width: 15rem;
          line-height: 1.5;
          transition: opacity .33s ease-in;
          opacity: 1;
          font-size: 1.25rem
        }

        div:has(.price) {
          grid-column: auto;
          display: flex!important;
          margin-top: 4px;
        }
      }
    }
  `}
`;

const NonStickyPricingBarStyles = styled(PricingBarBase)`
  ${({ ...props }) => css`
    .pricing-bar__wrapper .title {
      display: none;
    }
    
    &.sticky-visible {
      opacity: 0;
      pointer-events: none;
    }
  `}
`;

const StickyPricingBarStyles = styled(PricingBarBase)`
  ${({ ...props }) => css`
    position: fixed;
    top: -1px;
    left: 0;
    width: 100%;
    z-index: 98;

    .pricing-bar__wrapper div:has(.price) {
      display: none;
    }
  `}
`;

export const PricingBar = ({ page }) => {
  const [isSticky, setIsSticky] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);
  const ref = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        // rect.bottom < 0 means the entire pricing bar is above the viewport
        setIsSticky(rect.bottom < 0);
      }
  
      const lastButton = document.querySelector(".quote-submit-btn:last-of-type");
      if (lastButton) {
        const rect = lastButton.getBoundingClientRect();
        setShouldHide(rect.top < window.innerHeight && rect.bottom > 0);
      }
    };
  
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
  
    // Run initially in case the page loads mid-scroll
    handleScroll();

    // Observer for hidden state based on bottom quote button viewport visibility
    const buttonObserver = new IntersectionObserver(
      ([e]) => setShouldHide(e.isIntersecting),
      { threshold: [0] }
    )

    const bottomQuoteButtons = document.querySelectorAll(".quote-submit-btn")
    let lastButton
    if (bottomQuoteButtons.length > 0) {
      lastButton = bottomQuoteButtons[bottomQuoteButtons.length - 1];
    }

    if (lastButton) {
      buttonObserver.observe(lastButton)
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);

      if (lastButton) {
        buttonObserver.unobserve(lastButton)
      }
    };
  }, []);

  const PricingBarContent = () => (
    <Section>
      <Container>
        <div className="pricing-bar__wrapper">
          <h2 className="title">
            <HtmlContent htmlString={page.title} />
          </h2>

          {(page.productData.productMeta?.hirePrice?.price ||
            page.productData.productMeta?.hirePrice?.priceOnApplication ||
            page.productData.productMeta?.buyPrice?.price ||
            page.productData.productMeta?.buyPrice?.priceOnApplication) && (
            <ProductPrice
              hirePrice={page.productData.productMeta.hirePrice}
              buyPrice={page.productData.productMeta.buyPrice}
              inStock={page.productData.productMeta.stackStatus}
              showStockIcon={true}
            />
          )}

          <a
            href="javascript:void(0)"
            onClick={navigateToQuoteForm}
            className="add-to-quote button"
          >
            Request a free quote
          </a>

          {!page.productData.hidePricesMayVaryText && (
            <PricesMayVary />
          )}
        </div>
      </Container>
    </Section>
  );

  return (
    <div ref={ref}>
      <Helmet
        bodyAttributes={{
          class: "no-sticky-usp-bar",
        }}
      />
      <NonStickyPricingBarStyles className={`pricing-bar ${shouldHide ? "should-hide" : ""} ${isSticky ? 'sticky-visible' : 'sticky-not-visible'}`}>
        <PricingBarContent />
      </NonStickyPricingBarStyles>

      {isSticky && (
        <StickyPricingBarStyles className={`pricing-bar sticky ${shouldHide ? "should-hide" : ""} ${isSticky ? 'sticky-visible' : 'sticky-not-visible'}`}>
          <PricingBarContent />
        </StickyPricingBarStyles>
      )}
    </div>
  );
};
